import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicSeoAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import { handleApiError, showToast } from '../../utils/helpers';
import { Search, Check, X } from 'lucide-react';

const PublicSeoReviewPage = () => {
  const { token } = useParams();
  const { isRTL } = useLanguage();

  const [plan, setPlan] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await publicSeoAPI.getByToken(token);
      setPlan(res.data.plan);
      setKeywords(res.data.keywords || []);
    } catch (err) {
      console.error('Error fetching SEO plan:', err);
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveKeyword = async (keywordId) => {
    try {
      setActionLoadingId(keywordId);
      await publicSeoAPI.approveKeyword(token, keywordId);
      await fetchData();
      showToast.success(isRTL ? 'تم اعتماد الكلمة بنجاح' : 'Keyword approved successfully');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRejectKeyword = async (keywordId) => {
    let comment = '';
    if (typeof window !== 'undefined') {
      comment =
        window.prompt(
          isRTL ? 'يمكنك كتابة سبب رفض هذه الكلمة (اختياري):' : 'You can add a reason for rejecting this keyword (optional):'
        ) || '';
    }

    try {
      setActionLoadingId(keywordId);
      await publicSeoAPI.rejectKeyword(token, keywordId, { comment: comment || '' });
      await fetchData();
      showToast.success(isRTL ? 'تم رفض الكلمة' : 'Keyword rejected');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoadingId(null);
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <Card className="p-8 max-w-md text-center">
          <div className="flex justify-center mb-4">
            <span className="flex w-16 h-16 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <X className="w-8 h-8" />
            </span>
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">
            {isRTL ? 'خطأ في تحميل خطة الـ SEO' : 'Error loading SEO plan'}
          </h1>
          <p className="text-rose-600 mb-4">{error}</p>
        </Card>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <Card className="p-8 max-w-md text-center">
          <div className="flex justify-center mb-4">
            <span className="flex w-16 h-16 items-center justify-center rounded-full bg-slate-100 text-slate-500">
              <Search className="w-8 h-8" />
            </span>
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">
            {isRTL ? 'الخطة غير موجودة' : 'Plan not found'}
          </h1>
          <p className="text-slate-600">
            {isRTL ? 'الرابط غير صحيح أو انتهت صلاحيته.' : 'The link is invalid or has expired.'}
          </p>
        </Card>
      </div>
    );
  }

  const approvedCount = keywords.filter((k) => k.status === 'approved').length;
  const rejectedCount = keywords.filter((k) => k.status === 'rejected').length;
  const proposedCount = keywords.filter((k) => k.status === 'proposed' || !k.status || k.status === 'draft').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Header Card */}
        <Card className="overflow-hidden">
          <div className="p-6 bg-gradient-to-r from-amber-50 via-white to-sky-50 border-b border-slate-200">
            <div className="flex items-center gap-4">
              <span className="flex w-12 h-12 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                <Search className="w-6 h-6" />
              </span>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  {isRTL ? 'مراجعة كلمات SEO' : 'Review SEO Keywords'}
                </h1>
                <p className="text-slate-600 text-sm mt-1">
                  {isRTL ? 'العميل:' : 'Client:'} {plan.client_name || (isRTL ? 'بدون اسم' : 'Unnamed')}
                </p>
                <p className="text-slate-600 text-sm">
                  {plan.name}
                  {(plan.month || plan.year) && (
                    <span className="text-slate-400 text-xs ml-1">
                      ({plan.month ? `${plan.month}/` : ''}
                      {plan.year || ''})
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                <Check className="w-3.5 h-3.5" />
                {isRTL ? 'معتمدة' : 'Approved'}: {approvedCount}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-100">
                <X className="w-3.5 h-3.5" />
                {isRTL ? 'مرفوضة' : 'Rejected'}: {rejectedCount}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                {isRTL ? 'قيد المراجعة' : 'Pending review'}: {proposedCount}
              </span>
            </div>
          </div>
        </Card>

        {/* Keywords Table */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50/50">
            <h2 className="text-sm font-semibold text-slate-800">
              {isRTL ? 'الكلمات المفتاحية' : 'Keywords'}
            </h2>
          </div>
          <div className="p-4">
            {keywords.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">
                {isRTL
                  ? 'لا توجد كلمات SEO متاحة للمراجعة في هذه الخطة.'
                  : 'There are no SEO keywords to review in this plan.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                        {isRTL ? 'الكلمة المفتاحية' : 'Keyword'}
                      </th>
                      <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold text-slate-600">
                        {isRTL ? 'الحالة' : 'Status'}
                      </th>
                      <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold text-slate-600">
                        {isRTL ? 'يُنشر؟' : 'Publish'}
                      </th>
                      <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold text-slate-600">
                        {isRTL ? 'تعليق العميل' : 'Client comment'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                        {isRTL ? 'الإجراء' : 'Action'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {keywords.map((k) => (
                      <tr key={k.id} className="hover:bg-slate-50/80">
                        <td className="px-4 py-2.5 text-slate-900 font-medium max-w-[200px] md:max-w-xs truncate">
                          {k.keyword}
                        </td>
                        <td className="hidden md:table-cell px-4 py-2.5">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              k.status === 'approved'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                : k.status === 'rejected'
                                ? 'bg-rose-50 text-rose-700 border border-rose-100'
                                : 'bg-amber-50 text-amber-700 border border-amber-100'
                            }`}
                          >
                            {k.status === 'approved'
                              ? isRTL ? 'معتمدة' : 'Approved'
                              : k.status === 'rejected'
                              ? isRTL ? 'مرفوضة' : 'Rejected'
                              : isRTL ? 'قيد المراجعة' : 'Pending'}
                          </span>
                        </td>
                        <td className="hidden md:table-cell px-4 py-2.5 text-slate-700">
                          {k.publish ? (isRTL ? 'نعم' : 'Yes') : (isRTL ? 'لا' : 'No')}
                        </td>
                        <td className="hidden md:table-cell px-4 py-2.5 text-slate-700 max-w-xs truncate">
                          {k.client_comment || <span className="text-slate-400">-</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          {(k.status === 'proposed' || !k.status || k.status === 'draft') && (
                            <div className="flex flex-col sm:flex-row gap-2">
                              <Button
                                size="sm"
                                variant="success"
                                disabled={actionLoadingId === k.id}
                                loading={actionLoadingId === k.id}
                                onClick={() => handleApproveKeyword(k.id)}
                                className="inline-flex items-center gap-1.5"
                              >
                                <Check className="w-3.5 h-3.5" />
                                {isRTL ? 'اعتماد' : 'Approve'}
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={actionLoadingId === k.id}
                                loading={actionLoadingId === k.id}
                                onClick={() => handleRejectKeyword(k.id)}
                                className="inline-flex items-center gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50"
                              >
                                <X className="w-3.5 h-3.5" />
                                {isRTL ? 'رفض' : 'Reject'}
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PublicSeoReviewPage;
